/**
 * Authentication service layer.
 * Abstracts all Firebase Auth operations from the rest of the application.
 */
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  updateProfile,
} from 'firebase/auth';
import { auth } from './firebase';
import { handleServiceError } from '../utils/error-handler.util';

/**
 * Register a new user with email and password.
 * @param {string} email - The user's email address.
 * @param {string} password - The user's chosen password.
 * @param {string} displayName - The user's display name.
 * @returns {Promise<import('firebase/auth').UserCredential>} The Firebase user credential.
 */
export const signUp = async (email, password, displayName) => {
  try {
    const credential = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(credential.user, { displayName });
    return credential;
  } catch (error) {
    handleServiceError(error, 'Sign up');
  }
};

/**
 * Sign in an existing user with email and password.
 * @param {string} email - The user's email address.
 * @param {string} password - The user's password.
 * @returns {Promise<import('firebase/auth').UserCredential>} The Firebase user credential.
 */
export const signIn = async (email, password) => {
  try {
    const credential = await signInWithEmailAndPassword(auth, email, password);
    return credential;
  } catch (error) {
    handleServiceError(error, 'Sign in');
  }
};

/**
 * Sign out the currently authenticated user.
 * @returns {Promise<void>}
 */
export const signOut = async () => {
  try {
    await firebaseSignOut(auth);
  } catch (error) {
    handleServiceError(error, 'Sign out');
  }
};

/**
 * Send a password reset email to the specified address.
 * @param {string} email - The email address to send the reset link to.
 * @returns {Promise<void>}
 */
export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    handleServiceError(error, 'Password reset');
  }
};

/**
 * Get the currently authenticated user.
 * @returns {import('firebase/auth').User|null} The current user or null if not authenticated.
 */
export const getCurrentUser = () => {
  return auth.currentUser;
};

/**
 * Subscribe to authentication state changes.
 * @param {function} callback - Called with the Firebase user (or null) on state change.
 * @returns {function} Unsubscribe function to stop listening.
 */
export const onAuthStateChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};
